/* eslint-disable @typescript-eslint/no-explicit-any */

import { useAgreementPlansByIntegrationId, useProductsV2, useErpPlans } from '@partiu/api/dashboard/hooks.ts';
import { useState, useRef, useMemo, useCallback } from 'react';
import { Card, Box, HStack, Textarea, InputGroup, VStack, Text } from '@chakra-ui/react';

import { Step0PartiuList, Step1Products, Step2Benefits, Step3ConfiguredPlans, Step3SVAList, Step4Simulators, ValueRangeFilter, ClientNumberFilter, StepNavigation } from './components';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';

export function ProductsAndPlans() {
  const { id } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const { data: productsV2, refetch: refetchProductsV2 } = useProductsV2(
    {
      page: 1,
      page_size: 300,
      agreement: id?.toString() || '',
      include_inactive: true,
    },
    { enabled: Boolean(id), refetchOnMount: false } // Só refetch em create/edit via invalidateQueries
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [clientsSortOrder, setClientsSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [minValue, setMinValue] = useState<number | null>(null);
  const [maxValue, setMaxValue] = useState<number | null>(null);
  const [minClientNumber, setMinClientNumber] = useState<number | null>(null);
  const [maxClientNumber, setMaxClientNumber] = useState<number | null>(null);
  const createRef = useRef<{
    open: (data?: any, options?: { showBenefitsSelector?: boolean; benefit?: any }) => void;
    close: () => void;
  } | null>(null);

  const clearValueFilter = useCallback(() => {
    setMinValue(null);
    setMaxValue(null);
  }, []);

  const clearClientNumberFilter = useCallback(() => {
    setMinClientNumber(null);
    setMaxClientNumber(null);
  }, []);

  const isValueFilterActive = minValue !== null || maxValue !== null;
  const isClientNumberFilterActive = minClientNumber !== null || maxClientNumber !== null;
  const hasPlanListFilter =
    Boolean(searchTerm?.trim()) || isValueFilterActive || isClientNumberFilterActive;

  const { data: productsERP, isLoading, refetch: refetchProductsERP } = useAgreementPlansByIntegrationId(
    { page: 1, page_size: 300, is_active: 'True' },
    { enabled: Boolean(id), refetchOnMount: false } // Só refetch em create/edit via invalidateQueries
  );

  const { data: erpData } = useErpPlans();
  const erpBySlug = useMemo(() => {
    const map = new Map<
      string,
      { value_plan: number; count_clients_active: number; count_clients: number; name?: string }
    >();

    if (!erpData?.plans?.length) return map;

    for (const p of erpData.plans) {
      const rawSlug = p.slug;
      const slug = typeof rawSlug === 'string' ? rawSlug.trim().toLowerCase() : '';
      const rawName = p.name;
      const nameNorm = typeof rawName === 'string' ? rawName.trim().toLowerCase() : '';

      if (!slug && !nameNorm) continue;

      const valuePlan =
        p.price != null
          ? p.price
          : p.value != null
          ? parseFloat(String(p.value).replace(',', '.'))
          : 0;

      const countActive = p.count_clients_active ?? 0;
      const countTotal = p.count_clients ?? 0;

      const entry = {
        value_plan: Number.isNaN(valuePlan) ? 0 : valuePlan,
        count_clients_active: countActive,
        count_clients: countTotal,
        name: p.name,
      };

      if (slug) map.set(slug, entry);
      if (nameNorm && nameNorm !== slug) map.set(nameNorm, entry);
    }

    return map;
  }, [erpData?.plans]);

  const enrichedProductsERP = useMemo(() => {
    const raw = productsERP?.results ?? productsERP ?? [];
    const list = Array.isArray(raw) ? raw : [];

    return list.map((plan: any) => {
      const rawSlug = plan.plan_slug_integration ?? plan.slug;
      const slug =
        typeof rawSlug === 'string' ? rawSlug.trim().toLowerCase() : String(rawSlug || '');

      const erp = slug ? erpBySlug.get(slug) : undefined;
      if (!erp) return plan;

      return {
        ...plan,
        value_plan: erp.value_plan,
        count_clients_active: erp.count_clients_active,
        count_clients: erp.count_clients,
        name_plan: plan.name_plan || erp.name || plan.name_plan,
      };
    });
  }, [productsERP, erpBySlug]);

  const filteredAndSortedProductsERP = useMemo(() => {
    const productERPSorted =
      enrichedProductsERP.filter((plan: any) => {
        // Filtro por termo de busca
        const searchMatch = !searchTerm?.trim() || (() => {
          const searchLines = searchTerm
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

          if (searchLines.length === 0) return true;

          return searchLines.some((searchLine) => {
            const searchLower = searchLine.toLowerCase();

            const planValue =
              plan.value_plan?.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }) || '';
            const planValueNumber = plan.value_plan?.toString() || '';

            return (
              plan.id_plan?.toString().toLowerCase().includes(searchLower) ||
              plan.name_plan?.toLowerCase().includes(searchLower) ||
              plan.plan_slug_integration?.toLowerCase().includes(searchLower) ||
              planValue.toLowerCase().includes(searchLower) ||
              planValueNumber.toLowerCase().includes(searchLower)
            );
          });
        })();

        // Filtro por range de valores
        const valueMatch = (() => {
          const planValue = parseFloat(plan.value_plan) || 0;

          if (minValue !== null && maxValue !== null) {
            return planValue >= minValue && planValue <= maxValue;
          } else if (minValue !== null) {
            return planValue >= minValue;
          } else if (maxValue !== null) {
            return planValue <= maxValue;
          }

          return true;
        })();

        // Filtro por range de número de cliente
        const clientNumberMatch = (() => {
          const clientNumber = parseInt(plan.count_clients_active) || 0;

          if (minClientNumber !== null && maxClientNumber !== null) {
            return clientNumber >= minClientNumber && clientNumber <= maxClientNumber;
          } else if (minClientNumber !== null) {
            return clientNumber >= minClientNumber;
          } else if (maxClientNumber !== null) {
            return clientNumber <= maxClientNumber;
          }

          return true;
        })();

        return searchMatch && valueMatch && clientNumberMatch;
      }) || [];

    // Ordenação removida daqui - será feita no Step1Products

    return productERPSorted;
  }, [enrichedProductsERP, searchTerm, minValue, maxValue, minClientNumber, maxClientNumber]);

  const filteredERPWithProducts = useMemo(
    () => filteredAndSortedProductsERP.filter((plan: any) => plan.products && plan.products.length > 0),
    [filteredAndSortedProductsERP]
  );

  const productsNonSVA = useMemo(
    () => ((productsV2?.results || []) as any[]).filter((product: any) => product.default_product?.id !== 4),
    [productsV2?.results]
  );

  const productsSVA = useMemo(
    () => (productsV2?.results || []).filter((product: any) => product.default_product?.id === 4) || [],
    [productsV2?.results]
  );

  const refetchAll = useCallback(() => {
    refetchProductsV2();
    refetchProductsERP();
  }, [refetchProductsV2, refetchProductsERP]);

  const refetchAllAsync = useCallback(async () => {
    await Promise.all([refetchProductsV2(), refetchProductsERP()]);
  }, [refetchProductsV2, refetchProductsERP]);

  const steps = [
    'Módulos Disponíveis',
    'Configurar Planos',
    'Gerenciar SVAs',
    'SVAs nos Planos',
    'Revisar Configuração',
    'Simuladores',
  ];

  return (
    <>
      <Card marginTop={0} gap={0} padding={4} width={'100%'} maxW="100%">
        <Box width={'100%'} mb={4}>
          <StepNavigation
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            steps={steps}
          />
        </Box>

        {/* Títulos e Filtros */}
        <Box width="100%" mt={2} mb={0}>
          <VStack spacing={3} width="100%" align="stretch">
            {/* Título para Step 0 (sem filtros) - removido */}

            {/* Título para Step 2 (sem filtros) */}
            {activeStep === 2 && (
              <Box width="100%">
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Gerencie seus produtos SVAs por categoria
                </Text>
              </Box>
            )}

            {/* Títulos para Steps 1, 3, 4 e 5 */}
            {(activeStep === 1 || activeStep === 3 || activeStep === 4 || activeStep === 5) && (
              <Box width="100%">
                {activeStep === 1 && (
                  <>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Configure os módulos disponíveis em cada plano
                    </Text>
                  </>
                )}
                {activeStep === 3 && (
                  <>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Adicione produtos SVAs aos seus planos
                    </Text>
                  </>
                )}
                {activeStep === 4 && (
                  <>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Revise todos os planos configurados
                    </Text>
                  </>
                )}
                {activeStep === 5 && (
                  <>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                      Simule a visualização dos planos nas medalhas Bronze, Prata e Ouro
                    </Text>
                  </>
                )}
              </Box>
            )}

            {/* Textarea e Filtros para Steps 1, 3, 4 e 5 */}
            {(activeStep === 1 || activeStep === 3 || activeStep === 4 || activeStep === 5) && (
              <HStack spacing={3} width="100%" align="flex-start" flexWrap="wrap">
                {/* Textarea de busca */}
                <Box flex="1" minW="300px">
                  <InputGroup size="sm">
                    <Textarea
                      placeholder="Digite um plano por linha (busca por ID, nome ou valor)"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: 'green.500',
                        boxShadow: '0 0 0 1px green.500',
                      }}
                      minH="60px"
                      maxH="100px"
                      resize="vertical"
                      fontSize="sm"
                      lineHeight="1.4"
                    />
                  </InputGroup>
                </Box>

                {/* Botões de Filtro */}
                <HStack spacing={2} flex="0 0 auto" flexWrap="wrap">
                  <ValueRangeFilter
                    minValue={minValue}
                    maxValue={maxValue}
                    onMinValueChange={setMinValue}
                    onMaxValueChange={setMaxValue}
                    onClear={clearValueFilter}
                    isActive={isValueFilterActive}
                  />
                  <ClientNumberFilter
                    minClientNumber={minClientNumber}
                    maxClientNumber={maxClientNumber}
                    onMinClientNumberChange={setMinClientNumber}
                    onMaxClientNumberChange={setMaxClientNumber}
                    onClear={clearClientNumberFilter}
                    isActive={isClientNumberFilterActive}
                  />
                </HStack>
              </HStack>
            )}

            {(activeStep === 1 || activeStep === 3 || activeStep === 4 || activeStep === 5) && (searchTerm?.trim() || sortOrder || isValueFilterActive || isClientNumberFilterActive) && (
              <Box fontSize="xs" color="gray.500" px={2}>
                {searchTerm?.trim() && (
                  <span>
                    Buscando por {searchTerm.split('\n').filter((line) => line.trim()).length} termo(s) de busca
                  </span>
                )}
                {searchTerm?.trim() && (sortOrder || isValueFilterActive || isClientNumberFilterActive) && <span> • </span>}
                {sortOrder && (
                  <span>Ordenado por valor: {sortOrder === 'asc' ? 'Menor para maior' : 'Maior para menor'}</span>
                )}
                {(searchTerm?.trim() || sortOrder) && (isValueFilterActive || isClientNumberFilterActive) && <span> • </span>}
                {isValueFilterActive && (
                  <span>
                    Filtro de valor: {minValue ? `≥ R$ ${minValue.toFixed(2).replace('.', ',')}` : 'Qualquer'} até{' '}
                    {maxValue ? `≤ R$ ${maxValue.toFixed(2).replace('.', ',')}` : 'Qualquer'}
                  </span>
                )}
                {(searchTerm?.trim() || sortOrder || isValueFilterActive) && isClientNumberFilterActive && <span> • </span>}
                { isClientNumberFilterActive && (
                  <span>
                    Filtro de cliente: {minClientNumber ? `≥ ${minClientNumber.toLocaleString('pt-BR')}` : 'Qualquer'} até{' '}
                    {maxClientNumber ? `≤ ${maxClientNumber.toLocaleString('pt-BR')}` : 'Qualquer'}
                  </span>
                )}
              </Box>
            )}
          </VStack>
        </Box>

        {/* Step Content */}
        <Box width="100%">
          {activeStep === 0 && (
            <Step0PartiuList productsV2={(productsV2?.results || []) as any} isLoading={isLoading} />
          )}

          {activeStep === 1 && (
            <Step1Products
              isLoading={isLoading}
              refetch={refetchAll}
              products={productsNonSVA}
              filteredProductsERP={filteredAndSortedProductsERP}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              clientsSortOrder={clientsSortOrder}
              setClientsSortOrder={setClientsSortOrder}
              hasListFilter={hasPlanListFilter}
            />
          )}

          {activeStep === 2 && (
            <Step3SVAList
              refetch={refetchAllAsync}
              products={productsSVA}
              isLoading={isLoading}
              createRef={createRef}
            />
          )}

          {activeStep === 3 && (
            <Step2Benefits
              products={productsSVA}
              filteredAndSortedProductsERP={filteredAndSortedProductsERP}
              refetch={refetchAllAsync}
              createRef={createRef}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              clientsSortOrder={clientsSortOrder}
              setClientsSortOrder={setClientsSortOrder}
              hasListFilter={hasPlanListFilter}
            />
          )}

          {activeStep === 4 && (
            <Step3ConfiguredPlans
              products={(productsV2?.results || []) as any}
              filteredAndSortedProductsERP={filteredERPWithProducts}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          )}

          {activeStep === 5 && (
            <Step4Simulators
              products={((productsV2?.results || []) as any)}
              filteredAndSortedProductsERP={filteredERPWithProducts}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          )}
        </Box>
      </Card>
    </>
  );
}

export { ProductsAndPlans as ProductsAndPlansContent };
